/**
 * Admin – Partner-Onboarding (NT-STREAMER-ONBOARD-01)
 *
 * Legt neue Partner-Streamer an, ohne Deployment/ENV-Änderung/manuelles SQL.
 *
 * Routen (Backend):
 *   GET   /api/v1/admin/streamers
 *   POST  /api/v1/admin/streamers/lookup
 *   POST  /api/v1/admin/streamers
 *   PATCH /api/v1/admin/streamers/:id
 *   PATCH /api/v1/admin/streamers/:id/active
 */

import { api, ApiError } from '../../services/api.js';
import { showToast } from '../../utils/toast.js';

// ─── Typen ────────────────────────────────────────────────────────────────────

interface TwitchLookupPreview {
  twitchId: string;
  login: string;
  displayName: string;
  profileImageUrl: string;
  broadcasterType: string;
}

interface PartnerAdminView {
  id: string;
  twitchChannelId: string;
  twitchUsername: string;
  displayName: string;
  slug: string;
  isActive: boolean;
  isLive: boolean;
  lastLiveAt: string | null;
  createdAt: string;
}

// ─── Modulzustand ─────────────────────────────────────────────────────────────

let containerId = '';
let lastLookup: TwitchLookupPreview | null = null;

// ─── Einstiegspunkt ───────────────────────────────────────────────────────────

export function initAdminStreamers(targetContainerId: string): void {
  containerId = targetContainerId;
  lastLookup = null;
  renderSkeleton();
  void loadPartners();
}

// ─── Grundstruktur ────────────────────────────────────────────────────────────

function renderSkeleton(): void {
  const container = document.getElementById(containerId);
  if (!container) return;

  container.innerHTML = `
    <section style="margin-bottom:32px;">
      <h2 style="color:var(--accent-gold);font-size:15px;letter-spacing:0.08em;margin:0 0 12px;">
        PARTNER ANLEGEN
      </h2>
      <div style="display:flex;flex-direction:column;gap:10px;max-width:480px;">
        <label style="display:flex;flex-direction:column;gap:4px;font-size:12px;color:var(--text-muted);">
          TWITCH-LOGIN
          <div style="display:flex;gap:8px;">
            <input id="as-login" type="text" maxlength="25" placeholder="z.B. xsymme" style="${inputStyle()}flex:1;" autocomplete="off" />
            <button id="as-lookup-submit" style="${secondaryBtnStyle()}">PRÜFEN</button>
          </div>
        </label>

        <div id="as-lookup-preview"></div>

        <label id="as-slug-wrap" style="display:none;flex-direction:column;gap:4px;font-size:12px;color:var(--text-muted);">
          SLUG
          <input id="as-slug" type="text" maxlength="32" style="${inputStyle()}" autocomplete="off" />
        </label>
        <label id="as-displayname-wrap" style="display:none;flex-direction:column;gap:4px;font-size:12px;color:var(--text-muted);">
          ANZEIGENAME
          <input id="as-displayname" type="text" maxlength="40" style="${inputStyle()}" autocomplete="off" />
        </label>

        <div>
          <button id="as-create-submit" style="${primaryBtnStyle()}" disabled>PARTNER ANLEGEN</button>
        </div>

        <div id="as-checklist"></div>
      </div>
    </section>

    <section>
      <h2 style="color:var(--accent-gold);font-size:15px;letter-spacing:0.08em;margin:0 0 12px;">
        PARTNER
      </h2>
      <div id="as-partner-list">
        <p style="color:var(--text-muted);">LADE...</p>
      </div>
    </section>
  `;

  attachHandlers();
}

function inputStyle(): string {
  return 'padding:8px 10px;background:var(--bg-elevated);border:1px solid var(--border);' +
    'color:var(--text-primary);font-family:\'Share Tech Mono\',monospace;font-size:13px;outline:none;';
}

function primaryBtnStyle(): string {
  return 'border:1px solid var(--accent-green);color:var(--accent-green);background:transparent;' +
    'padding:8px 18px;cursor:pointer;font-family:\'Share Tech Mono\',monospace;font-size:13px;letter-spacing:0.05em;' +
    'border-radius:var(--radius-btn,6px);';
}

function secondaryBtnStyle(): string {
  return 'border:1px solid var(--accent-blue);color:var(--accent-blue);background:transparent;' +
    'padding:8px 18px;cursor:pointer;font-family:\'Share Tech Mono\',monospace;font-size:13px;letter-spacing:0.05em;' +
    'border-radius:var(--radius-btn,6px);white-space:nowrap;';
}

function smallBtnStyle(color: string): string {
  return `border:1px solid ${color};color:${color};background:transparent;padding:3px 10px;cursor:pointer;` +
    'font-family:\'Share Tech Mono\',monospace;font-size:11px;letter-spacing:0.05em;border-radius:var(--radius-btn,6px);';
}

// ─── Event-Bindung ────────────────────────────────────────────────────────────

function attachHandlers(): void {
  document.getElementById('as-lookup-submit')?.addEventListener('click', () => { void handleLookup(); });
  document.getElementById('as-create-submit')?.addEventListener('click', () => { void handleCreatePartner(); });

  const loginInput = document.getElementById('as-login') as HTMLInputElement | null;
  loginInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') void handleLookup();
  });
}

// ─── Twitch-Lookup ────────────────────────────────────────────────────────────

async function handleLookup(): Promise<void> {
  const loginInput = document.getElementById('as-login') as HTMLInputElement | null;
  if (!loginInput) return;

  const twitchLogin = loginInput.value.trim();
  if (!twitchLogin) {
    showToast('Bitte einen Twitch-Login eingeben.', 'error');
    return;
  }

  clearPreview();
  disableCreateButton();

  try {
    const result = await api.post<TwitchLookupPreview>('/admin/streamers/lookup', { twitchLogin });
    lastLookup = result;
    renderLookupPreview(result);
    prefillCreateFields(result);
    enableCreateButton();
  } catch (err) {
    lastLookup = null;
    showToast(err instanceof ApiError ? err.message : 'Twitch-Lookup fehlgeschlagen.', 'error');
  }
}

function clearPreview(): void {
  const previewEl = document.getElementById('as-lookup-preview');
  if (previewEl) previewEl.innerHTML = '';
  const slugWrap = document.getElementById('as-slug-wrap');
  const displayNameWrap = document.getElementById('as-displayname-wrap');
  if (slugWrap) slugWrap.style.display = 'none';
  if (displayNameWrap) displayNameWrap.style.display = 'none';
}

function disableCreateButton(): void {
  const btn = document.getElementById('as-create-submit') as HTMLButtonElement | null;
  if (btn) btn.disabled = true;
}

function enableCreateButton(): void {
  const btn = document.getElementById('as-create-submit') as HTMLButtonElement | null;
  if (btn) btn.disabled = false;
}

function renderLookupPreview(result: TwitchLookupPreview): void {
  const previewEl = document.getElementById('as-lookup-preview');
  if (!previewEl) return;

  previewEl.innerHTML = `
    <div style="display:flex;gap:12px;align-items:center;padding:10px;
                border:1px solid var(--border);background:var(--bg-elevated);">
      <img src="${escapeAttr(result.profileImageUrl)}" alt="" width="48" height="48"
           style="border-radius:var(--radius-btn,6px);" />
      <div>
        <div style="color:var(--text-primary);font-size:14px;">${escapeHtml(result.displayName)}</div>
        <div style="color:var(--text-muted);font-size:11px;">
          LOGIN: ${escapeHtml(result.login)} · CHANNEL-ID: ${escapeHtml(result.twitchId)} ·
          TYP: ${escapeHtml(result.broadcasterType || 'KEIN PARTNER/AFFILIATE')}
        </div>
      </div>
    </div>
  `;
}

function prefillCreateFields(result: TwitchLookupPreview): void {
  const slugWrap = document.getElementById('as-slug-wrap');
  const displayNameWrap = document.getElementById('as-displayname-wrap');
  const slugInput = document.getElementById('as-slug') as HTMLInputElement | null;
  const displayNameInput = document.getElementById('as-displayname') as HTMLInputElement | null;

  if (slugInput) slugInput.value = deriveSlugFromLogin(result.login);
  if (displayNameInput) displayNameInput.value = result.displayName;
  if (slugWrap) slugWrap.style.display = 'flex';
  if (displayNameWrap) displayNameWrap.style.display = 'flex';
}

function deriveSlugFromLogin(login: string): string {
  return login.toLowerCase().replace(/[^a-z0-9]/g, '-');
}

// ─── Partner anlegen ──────────────────────────────────────────────────────────

async function handleCreatePartner(): Promise<void> {
  if (!lastLookup) {
    showToast('Bitte zuerst einen Twitch-Login prüfen.', 'error');
    return;
  }

  const slugInput = document.getElementById('as-slug') as HTMLInputElement | null;
  const displayNameInput = document.getElementById('as-displayname') as HTMLInputElement | null;
  const trimmedSlug = slugInput?.value.trim();
  const trimmedDisplayName = displayNameInput?.value.trim();

  try {
    const partner = await api.post<PartnerAdminView>('/admin/streamers', {
      twitchLogin: lastLookup.login,
      slug: trimmedSlug !== undefined && trimmedSlug.length > 0 ? trimmedSlug : undefined,
      displayName: trimmedDisplayName !== undefined && trimmedDisplayName.length > 0 ? trimmedDisplayName : undefined,
    });
    showToast(`Partner "${partner.displayName}" angelegt (inaktiv).`, 'info');
    showChecklist();

    lastLookup = null;
    const loginInput = document.getElementById('as-login') as HTMLInputElement | null;
    if (loginInput) loginInput.value = '';
    clearPreview();
    disableCreateButton();

    void loadPartners();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Partner konnte nicht angelegt werden.', 'error');
  }
}

function showChecklist(): void {
  const el = document.getElementById('as-checklist');
  if (!el) return;

  // Reihenfolge bewusst so gewählt, dass sie zum tatsächlichen Code passt: Der
  // Twitch-OAuth-Callback des Streamer-Dashboards (dashboard.service.ts) verlangt
  // bereits beim Login isActive:true — ein Login-Versuch VOR der Aktivierung scheitert
  // mit 403. Erst aktivieren, dann kann sich der Streamer anmelden.
  el.innerHTML = `
    <div style="padding:10px;border:1px solid var(--accent-gold);background:var(--bg-elevated);font-size:12px;color:var(--text-primary);">
      <strong style="color:var(--accent-gold);">NÄCHSTE SCHRITTE — MANUELL</strong>
      <ol style="margin:8px 0 0;padding-left:18px;line-height:1.6;">
        <li>Nighttrade_Ops im Channel als Moderator eintragen (/mod nighttrade_ops)</li>
        <li>Hier in der Partner-Liste unten auf AKTIVIEREN klicken</li>
        <li>Erst danach kann sich der Streamer unter /streamer-dashboard per Twitch anmelden</li>
      </ol>
    </div>
  `;
}

// ─── Partner-Liste ────────────────────────────────────────────────────────────

async function loadPartners(): Promise<void> {
  const listEl = document.getElementById('as-partner-list');
  if (!listEl) return;

  try {
    const partners = await api.get<PartnerAdminView[]>('/admin/streamers');
    renderPartnerTable(partners);
  } catch (err) {
    listEl.innerHTML = `<p style="color:var(--accent-red);">${escapeHtml(
      err instanceof ApiError ? err.message : 'Fehler beim Laden der Partner.',
    )}</p>`;
  }
}

function renderPartnerTable(partners: PartnerAdminView[]): void {
  const listEl = document.getElementById('as-partner-list');
  if (!listEl) return;

  if (partners.length === 0) {
    listEl.innerHTML = `<p style="color:var(--text-muted);">KEINE PARTNER VORHANDEN.</p>`;
    return;
  }

  listEl.innerHTML = `
    <div style="overflow-x:auto;">
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead>
          <tr style="text-align:left;color:var(--text-muted);border-bottom:1px solid var(--border);">
            <th style="padding:6px 8px;">ANZEIGENAME</th>
            <th style="padding:6px 8px;">SLUG</th>
            <th style="padding:6px 8px;">CHANNEL-ID</th>
            <th style="padding:6px 8px;">STATUS</th>
            <th style="padding:6px 8px;">LIVE</th>
            <th style="padding:6px 8px;"></th>
          </tr>
        </thead>
        <tbody>
          ${partners.map((p) => partnerRowHtml(p)).join('')}
        </tbody>
      </table>
    </div>
  `;

  listEl.querySelectorAll<HTMLButtonElement>('[data-as-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.asToggle;
      const nextActive = btn.dataset.asNextActive === 'true';
      if (id) void handleToggleActive(id, nextActive);
    });
  });
}

function partnerRowHtml(p: PartnerAdminView): string {
  const statusColor = p.isActive ? 'var(--accent-green)' : 'var(--text-muted)';
  const statusLabel = p.isActive ? 'AKTIV' : 'INAKTIV';
  const toggleLabel = p.isActive ? 'DEAKTIVIEREN' : 'AKTIVIEREN';
  const toggleColor = p.isActive ? 'var(--accent-red)' : 'var(--accent-green)';
  const liveLabel = p.isLive ? 'LIVE' : '–';
  const liveColor = p.isLive ? 'var(--accent-red)' : 'var(--text-muted)';

  return `
    <tr style="border-bottom:1px solid var(--border);">
      <td style="padding:6px 8px;color:var(--text-primary);">${escapeHtml(p.displayName)}</td>
      <td style="padding:6px 8px;color:var(--text-muted);">${escapeHtml(p.slug)}</td>
      <td style="padding:6px 8px;color:var(--text-muted);">${escapeHtml(p.twitchChannelId)}</td>
      <td style="padding:6px 8px;color:${statusColor};">${statusLabel}</td>
      <td style="padding:6px 8px;color:${liveColor};">${liveLabel}</td>
      <td style="padding:6px 8px;text-align:right;">
        <button data-as-toggle="${p.id}" data-as-next-active="${p.isActive ? 'false' : 'true'}"
                style="${smallBtnStyle(toggleColor)}">
          ${toggleLabel}
        </button>
      </td>
    </tr>
  `;
}

async function handleToggleActive(id: string, nextActive: boolean): Promise<void> {
  try {
    await api.patch(`/admin/streamers/${id}/active`, { isActive: nextActive });
    showToast(nextActive ? 'Partner aktiviert.' : 'Partner deaktiviert.', 'info');
    void loadPartners();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Status konnte nicht geändert werden.', 'error');
  }
}

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(str: string): string {
  return str.replace(/"/g, '&quot;');
}
